import { createSlice } from '@reduxjs/toolkit'
import { getAllActivity, swipeWithAction, swipeWithoutAction, createActivity,
  getActivityStats,
  createBulkActivities,
  searchActivities,
  getUserActivities,
  getActivityById,
  deleteActivity,
  getRecentActivities,
  getGdprConsents } from '../actions/activityActions.js'

const initialState = {
  activities: [],
  swipeWithAction: [],
  swipeWithoutAction: [],
  userActivities: [],
  searchResults: [],
  activityStats: null,
  currentActivity: null,
  recentActivities: [],
  recentActivitiesPagination: {
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  },
  gdprConsents: [],
  loading: false,
  error: null,
}

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    resetActivityState: state => {
      state.activities = []
      state.swipeWithAction = []
      state.swipeWithoutAction = []
      state.userActivities = []
      state.searchResults = []
      state.activityStats = null
      state.currentActivity = null
      state.recentActivities = []
      state.recentActivitiesPagination = {
        total: 0,
        page: 1,
        limit: 10,
        pages: 0,
      }
      state.gdprConsents = []
      state.loading = false
      state.error = null
    },
    getActivityRequest: state => {
      state.loading = true
      state.error = null
    },
    getActivitySuccess: (state, action) => {
      state.loading = false
      state.activities = action.payload
    },
    getActivityFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    setCurrentActivity: (state, action) => {
      state.currentActivity = action.payload
    },
    clearSearchResults: state => {
      state.searchResults = []
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAllActivity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllActivity.fulfilled, (state, action) => {
        state.activities = action.payload.data
        state.loading = false
      })
      .addCase(getAllActivity.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(swipeWithAction.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(swipeWithAction.fulfilled, (state, action) => {
        state.activities = action.payload
        state.loading = false
      })
      .addCase(swipeWithAction.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(swipeWithoutAction.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(swipeWithoutAction.fulfilled, (state, action) => {
        state.activities = action.payload
        state.loading = false
      })
      .addCase(swipeWithoutAction.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })

      // Activity Stats
      .addCase(getActivityStats.pending, state => {
        state.loading = true
      })
      .addCase(getActivityStats.fulfilled, (state, action) => {
        state.activityStats = action.payload.data
        state.loading = false
      })
      .addCase(getActivityStats.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })

      // User Activities
      .addCase(getUserActivities.pending, state => {
        state.loading = true
      })
      .addCase(getUserActivities.fulfilled, (state, action) => {
        state.userActivities = action.payload.data
        state.loading = false
      })

      // Single Activity
      .addCase(getActivityById.pending, state => {
        state.loading = true
        state.currentActivity = null
      })
      .addCase(getActivityById.fulfilled, (state, action) => {
        state.currentActivity = action.payload.data
        state.loading = false
      })

      // Search Activities
      .addCase(searchActivities.fulfilled, (state, action) => {
        state.searchResults = action.payload.data
      })

      // Create Activity
      .addCase(createActivity.fulfilled, (state, action) => {
        state.activities.unshift(action.payload.data)
      })

      // Create Bulk Activities
      .addCase(createBulkActivities.fulfilled, (state, action) => {
        state.activities = [...action.payload.data, ...state.activities]
      })

      // Delete Activity
      .addCase(deleteActivity.fulfilled, (state, action) => {
        state.activities = state.activities.filter(
          activity => activity._id !== action.payload.id
        )
        if (state.currentActivity?._id === action.payload.id) {
          state.currentActivity = null
        }
      })

      // Recent Activities (Admin)
      .addCase(getRecentActivities.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        // Handle backend response - activities might be in data array or data.activities
        const activities = Array.isArray(action.payload.data)
          ? action.payload.data
          : action.payload.data?.activities || []

        state.recentActivities = activities
        state.recentActivitiesPagination = action.payload.pagination || action.payload.data?.pagination || {
          total: activities.length,
          page: 1,
          limit: action.payload.limit || 10,
          pages: Math.ceil(activities.length / (action.payload.limit || 10)),
        }
        state.loading = false
      })
      .addCase(getRecentActivities.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })

      // GDPR Consents (Admin)
      .addCase(getGdprConsents.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getGdprConsents.fulfilled, (state, action) => {
        // Handle backend response - could be array or single object
        const data = action.payload.data || action.payload
        state.gdprConsents = Array.isArray(data) ? data : [data]
        state.loading = false
      })
      .addCase(getGdprConsents.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
  },
})

export const { resetActivityState, getActivityRequest, getActivitySuccess, getActivityFail, setCurrentActivity } = activitySlice.actions

export default activitySlice.reducer
